import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  styled,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useState, forwardRef, useEffect } from "react";
import { Section } from "@app/models/section.model";
import { SectionType } from "@app/constants";

const FooterContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "12px 24px",
  backgroundColor: "#fff",
  boxShadow: "0 -1px 3px rgba(0,0,0,0.12)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  zIndex: 1000,
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
  },
}));

const NavigationSection = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "row",
  maxWidth: "80%",
  flexWrap: "wrap",
  alignItems: "center",
  // gap: "16px",
  overflow: "auto",
  "&::-webkit-scrollbar": {
    height: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
}));

const PartContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  padding: "4px 0",
}));

const QuestionGroupContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
}));

// const PartLabel = styled(Typography)(({ theme }) => ({
//   fontSize: "14px",
//   fontWeight: 500,
//   color: "#666",
//   minWidth: "50px",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "12px",
//     minWidth: "40px",
//   },
// }));

const QuestionButton = styled(Button)<{
  isreview?: string;
  isanswered?: string;
  isactive?: string;
}>(({ theme, isreview, isanswered, isactive }) => ({
  minWidth: "36px",
  height: "36px",
  padding: "0",
  margin: "0 4px",
  borderRadius: isreview === "true" ? "50%" : "4px",
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  ...(isanswered === "true" && isactive !== "true" && {
    backgroundColor: "gray",
    color: "#fff",
  }),
}));

const NavigationArrow = styled(IconButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
  },
}));

interface ExamFooterProps {
  questionsBySectionType: Section[];
  currentQuestion: number;
  onQuestionChange: (question: number) => void;
  onReview: (isreview: boolean) => void;
  answeredQuestionValue: string | string[] | Record<number, string>;
  answeredQuestionId: number;
  sectionType: SectionType;
}

const ExamFooter = forwardRef<HTMLDivElement, ExamFooterProps>(
  (
    {
      questionsBySectionType,
      currentQuestion,
      onQuestionChange,
      onReview,
      answeredQuestionId,
      answeredQuestionValue,
      sectionType
    },
    ref
  ) => {
    const [reviewedQuestions, setReviewedQuestions] = useState<number[]>([]);
    const [answeredQuestions, setAnsweredQuestions] = useState<number[]>([]);

    if (!questionsBySectionType || !Array.isArray(questionsBySectionType)) {
      return (
        <FooterContainer ref={ref}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <NavigationArrow onClick={() => {}} disabled={true}>
              <ArrowBack />
            </NavigationArrow>
            <NavigationArrow onClick={() => {}} disabled={true}>
              <ArrowForward />
            </NavigationArrow>
          </Box>
        </FooterContainer>
      );
    }

    useEffect(() => {
      if (answeredQuestionValue && answeredQuestionId) {
        let answeredValue = answeredQuestionValue;

        if (typeof answeredQuestionValue === "string")
          answeredValue = (answeredQuestionValue as string).trim();

        if (answeredValue) {
          setAnsweredQuestions([...answeredQuestions, answeredQuestionId]);
        }
      } else if (!answeredQuestionValue && answeredQuestionId) {
        setAnsweredQuestions(
          answeredQuestions.filter((q) => q !== answeredQuestionId)
        );
      } else {
        setAnsweredQuestions([]);
      }
    }, [answeredQuestionValue, answeredQuestionId]);

    useEffect(() => {
      setReviewedQuestions([]);
      
    }, [sectionType]);

    const totalQuestions = questionsBySectionType.reduce((total, section) => {
      if (!section.questionGroups) return total;

      return (
        total +
        section.questionGroups.reduce((groupTotal, group) => {
          if (!group.questions) return groupTotal;

          return groupTotal + group.questions.length;
        }, 0)
      );
    }, 0);

    const isLastQuestion = currentQuestion === totalQuestions;

    const handleReviewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setReviewedQuestions([...reviewedQuestions, currentQuestion]);
      } else {
        setReviewedQuestions(
          reviewedQuestions.filter((q) => q !== currentQuestion)
        );
      }

      onReview(event.target.checked);
    };

    const handlePrevious = () => {
      if (currentQuestion > 1) {
        onQuestionChange(currentQuestion - 1);
      }
    };

    const handleNext = () => {
      if (currentQuestion < totalQuestions) {
        onQuestionChange(currentQuestion + 1);
      }
    };

    const getQuestionNumber = (
      sectionIndex: number,
      groupIndex: number,
      questionIndex: number
    ): number => {
      let questionNumber = 1;

      for (let i = 0; i < sectionIndex; i++) {
        const section = questionsBySectionType[i];

        if (!section.questionGroups) continue;

        for (let j = 0; j < section.questionGroups.length; j++) {
          const group = section.questionGroups[j];

          if (!group.questions) continue;

          questionNumber += group.questions.length;
        }
      }

      const currentSection = questionsBySectionType[sectionIndex];

      if (currentSection.questionGroups) {
        for (let j = 0; j < groupIndex; j++) {
          const group = currentSection.questionGroups[j];

          if (!group.questions) continue;

          questionNumber += group.questions.length;
        }
      }

      return questionNumber + questionIndex;
    };

    return (
      <FooterContainer ref={ref}>
        <FormControlLabel
          control={
            <Checkbox
              checked={reviewedQuestions.includes(currentQuestion)}
              onChange={handleReviewChange}
              color="primary"
            />
          }
          label="Review"
        />

        <NavigationSection>
          {questionsBySectionType.map((section, sectionIndex) => (
            <PartContainer key={sectionIndex}>
              {/* <PartLabel>{section.title || `Part ${sectionIndex + 1}`}</PartLabel> */}
              {section.questionGroups?.map((group, groupIndex) => (
                <QuestionGroupContainer key={groupIndex}>
                  {group.questions?.map((_, questionIndex) => {
                    const questionNumber = getQuestionNumber(
                      sectionIndex,
                      groupIndex,
                      questionIndex
                    );

                    return (
                      <QuestionButton
                        key={questionNumber}
                        variant={
                          currentQuestion === questionNumber
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => onQuestionChange(questionNumber)}
                        isreview={reviewedQuestions
                          .includes(questionNumber)
                          .toString()}
                        className={
                          answeredQuestions
                            .includes(questionNumber)
                            .toString() === "true"
                            ? "has-answered"
                            : ""
                        }
                        isanswered={answeredQuestions
                          .includes(questionNumber)
                          .toString()}
                        isactive={currentQuestion === questionNumber ? "true" : "false"}
                      >
                        {questionNumber}
                      </QuestionButton>
                    );
                  })}
                </QuestionGroupContainer>
              ))}
            </PartContainer>
          ))}
        </NavigationSection>

        <Box sx={{ display: "flex", gap: 1 }}>
          <NavigationArrow
            onClick={handlePrevious}
            disabled={currentQuestion === 1}
          >
            <ArrowBack />
          </NavigationArrow>
          <NavigationArrow onClick={handleNext} disabled={isLastQuestion}>
            <ArrowForward />
          </NavigationArrow>
        </Box>
      </FooterContainer>
    );
  }
);

ExamFooter.displayName = "ExamFooter";

export default ExamFooter;
